<div class="admin-users-page account-settings-page">
  @if (isInitialLoading) {
    <section class="page-card card">
      <div class="card-body py-5 text-center">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 mb-0">Loading users and access settings...</p>
      </div>
    </section>
  } @else if (hasInitialLoadError) {
    <app-api-load-error
      title="Could not load user access data"
      message="Some error occurred while loading the user table, roles, and available plans."
      retryLabel="Retry"
      (retry)="loadData()"
    ></app-api-load-error>
  } @else {
    <div class="d-grid gap-4">
      @if (createMessage) {
        <div class="alert alert-success mb-0">{{ createMessage }}</div>
      }
      @if (saveMessage) {
        <div class="alert alert-success mb-0">{{ saveMessage }}</div>
      }

      <section class="page-card card">
        <div class="card-body d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
          <div>
            <h3 class="section-title mb-0 bw-title-with-info">
              Users and access
              <app-info-tooltip compact placement="right" text="Manage platform roles, plan assignments, and account activity from one table."></app-info-tooltip>
            </h3>
          </div>

          <div class="d-flex flex-wrap gap-2 align-items-center">
            <span class="badge text-bg-secondary">{{ filteredUsers.length }} / {{ users.length }} accounts</span>
            <button type="button" class="btn btn-primary" (click)="openCreateModal()">New user</button>
          </div>
        </div>
      </section>

      @if (isCompactMode && filtersOpen) {
        <button
          type="button"
          class="admin-users-filters__scrim"
          aria-label="Close filters"
          (click)="closeFilters()"
        ></button>
      }

      <div class="admin-users-layout" [class.admin-users-layout--with-filters]="filtersOpen && !isCompactMode">
        @if (filtersOpen) {
          <aside
            class="admin-users-filters"
            [class.is-compact]="isCompactMode"
            [class.is-open]="filtersOpen"
          >
            <section class="page-card card admin-users-filters__panel">
              <div class="card-body d-grid gap-3">
                <div class="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h3 class="section-title mb-0 bw-title-with-info">
                      Filters
                      <app-info-tooltip compact placement="right" text="Narrow the user table by role, plan, status, and search terms."></app-info-tooltip>
                    </h3>
                  </div>
                  <button type="button" class="btn btn-sm btn-outline-secondary" (click)="closeFilters()">Close</button>
                </div>

                <div>
                  <label class="form-label">Search</label>
                  <input
                    class="form-control"
                    type="search"
                    [(ngModel)]="filters.search"
                    name="filters_search"
                    placeholder="Email, name, or nickname"
                  />
                </div>

                <div>
                  <label class="form-label">Role</label>
                  <select class="form-select" [(ngModel)]="filters.role" name="filters_role">
                    <option value="all">All roles</option>
                    @for (role of roles; track role) {
                      <option [ngValue]="role">{{ role }}</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="form-label">Plan</label>
                  <select class="form-select" [(ngModel)]="filters.planCode" name="filters_plan">
                    <option value="all">All plans</option>
                    @for (plan of plans; track plan.code) {
                      <option [ngValue]="plan.code">{{ plan.name }}</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="form-label">Account activity</label>
                  <select class="form-select" [(ngModel)]="filters.activity" name="filters_activity">
                    <option value="all">All accounts</option>
                    <option value="active">Active only</option>
                    <option value="inactive">Inactive only</option>
                  </select>
                </div>

                <div>
                  <label class="form-label">Plan status</label>
                  <select class="form-select" [(ngModel)]="filters.planStatus" name="filters_plan_status">
                    <option value="all">All statuses</option>
                    @for (status of planStatuses; track status) {
                      <option [ngValue]="status">{{ status }}</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="form-label">Email verification</label>
                  <select class="form-select" [(ngModel)]="filters.emailVerified" name="filters_email_verified">
                    <option value="all">All users</option>
                    <option value="verified">Verified only</option>
                    <option value="unverified">Unverified only</option>
                  </select>
                </div>

                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="resetFilters()"
                  [disabled]="!hasActiveFilters"
                >
                  Reset filters
                </button>
              </div>
            </section>
          </aside>
        }

        <section class="page-card card">
          <div class="card-body d-grid gap-3">
            <div class="d-flex justify-content-between align-items-center gap-3">
              <div class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="admin-users-table__icon-button"
                  [class.is-active]="filtersOpen"
                  [attr.aria-label]="filtersToggleLabel"
                  [title]="filtersToggleLabel"
                  (click)="toggleFilters()"
                >
                  <ion-icon [name]="filtersToggleIcon"></ion-icon>
                </button>
                <div>
                  <h3 class="section-title mb-0 bw-title-with-info">
                    Accounts table
                    <app-info-tooltip compact placement="right" text="Edit any user from the table. Filters are kept separate from the table layout."></app-info-tooltip>
                  </h3>
                </div>
              </div>
              <span class="admin-users-page__sort-status">Sorted by {{ currentSortLabel }} {{ sortDirection }}.</span>
            </div>

            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>
                      <button type="button" class="admin-users-table__sort" [class.is-active]="isSortedColumn('email')" (click)="toggleSort('email')">
                        <span>Email</span>
                        <ion-icon [name]="sortIcon('email')"></ion-icon>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="admin-users-table__sort" [class.is-active]="isSortedColumn('platformRole')" (click)="toggleSort('platformRole')">
                        <span>Role</span>
                        <ion-icon [name]="sortIcon('platformRole')"></ion-icon>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="admin-users-table__sort" [class.is-active]="isSortedColumn('plan')" (click)="toggleSort('plan')">
                        <span>Plan</span>
                        <ion-icon [name]="sortIcon('plan')"></ion-icon>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="admin-users-table__sort" [class.is-active]="isSortedColumn('planStatus')" (click)="toggleSort('planStatus')">
                        <span>Plan status</span>
                        <ion-icon [name]="sortIcon('planStatus')"></ion-icon>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="admin-users-table__sort" [class.is-active]="isSortedColumn('isActive')" (click)="toggleSort('isActive')">
                        <span>Activity</span>
                        <ion-icon [name]="sortIcon('isActive')"></ion-icon>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="admin-users-table__sort" [class.is-active]="isSortedColumn('emailVerified')" (click)="toggleSort('emailVerified')">
                        <span>Verified</span>
                        <ion-icon [name]="sortIcon('emailVerified')"></ion-icon>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="admin-users-table__sort" [class.is-active]="isSortedColumn('createdAt')" (click)="toggleSort('createdAt')">
                        <span>Created</span>
                        <ion-icon [name]="sortIcon('createdAt')"></ion-icon>
                      </button>
                    </th>
                    <th class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  @if (filteredUsers.length === 0) {
                    <tr>
                      <td colspan="8" class="py-4 text-center text-body-secondary">
                        No users match the current filters.
                      </td>
                    </tr>
                  } @else {
                    @for (user of filteredUsers; track user.publicId) {
                      <tr [class.table-active]="selectedUserPublicId === user.publicId">
                        <td>
                          <div class="fw-semibold">{{ user.email }}</div>
                          <div class="text-body-secondary small">
                            {{ user.nickname || user.name || 'No display name' }}
                          </div>
                        </td>
                        <td><span class="badge text-bg-primary-subtle">{{ user.platformRole }}</span></td>
                        <td>{{ user.plan.name }}</td>
                        <td>
                          <span class="badge text-bg-light border text-uppercase">{{ user.plan.status }}</span>
                        </td>
                        <td>
                          <span class="badge" [class.text-bg-success]="user.isActive" [class.text-bg-secondary]="!user.isActive">
                            {{ user.isActive ? 'Active' : 'Inactive' }}
                          </span>
                        </td>
                        <td>
                          <span class="badge" [class.text-bg-success]="user.emailVerified" [class.text-bg-warning]="!user.emailVerified">
                            {{ user.emailVerified ? 'Verified' : 'Pending' }}
                          </span>
                        </td>
                        <td>{{ user.createdAt | date: 'mediumDate' }}</td>
                        <td class="text-end">
                          <button type="button" class="btn btn-sm btn-outline-primary" (click)="openEditModal(user)">Edit</button>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>

    @if (activeModal) {
      <button type="button" class="admin-users-modal__scrim" aria-label="Close user modal" (click)="closeModal()"></button>
      <div class="admin-users-modal">
        <section class="page-card card admin-users-modal__dialog">
          <div class="card-body d-grid gap-3">
            <div class="d-flex justify-content-between align-items-start gap-3">
              <div>
                <h3 class="section-title mb-0 bw-title-with-info">
                  {{ activeModalTitle }}
                  <app-info-tooltip compact placement="right" [text]="activeModal === 'create' ? 'Provision a new account with its initial access settings.' : 'Adjust role, plan, and activity for the selected account.'"></app-info-tooltip>
                </h3>
              </div>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="closeModal()">Close</button>
            </div>

            @if (activeModal === 'create') {
              @if (createError) {
                <div class="alert alert-danger mb-0">{{ createError }}</div>
              }

              <form class="d-grid gap-3" (ngSubmit)="createUser()">
                <div>
                  <label class="form-label">Email</label>
                  <input class="form-control" type="email" [(ngModel)]="createForm.email" name="create_email" required />
                </div>

                <div>
                  <label class="form-label">Password</label>
                  <input class="form-control" type="password" [(ngModel)]="createForm.password" name="create_password" required />
                </div>

                <div>
                  <label class="form-label">Display name</label>
                  <input class="form-control" type="text" [(ngModel)]="createForm.name" name="create_name" />
                </div>

                <div class="row g-3">
                  <div class="col-sm-6">
                    <label class="form-label">Role</label>
                    <select class="form-select" [(ngModel)]="createForm.role" name="create_role">
                      @for (role of roles; track role) {
                        <option [ngValue]="role">{{ role }}</option>
                      }
                    </select>
                  </div>

                  <div class="col-sm-6">
                    <label class="form-label">Plan</label>
                    <select class="form-select" [(ngModel)]="createForm.planCode" name="create_plan">
                      @for (plan of plans; track plan.code) {
                        <option [ngValue]="plan.code">{{ plan.name }}</option>
                      }
                    </select>
                  </div>
                </div>

                <label class="form-check">
                  <input class="form-check-input" type="checkbox" [(ngModel)]="createForm.isActive" name="create_active" />
                  <span class="form-check-label">Create account as active</span>
                </label>

                <div class="d-flex flex-wrap gap-2 justify-content-end">
                  <button type="button" class="btn btn-outline-secondary" (click)="closeModal()">Cancel</button>
                  <button class="btn btn-primary" type="submit" [disabled]="isCreating">
                    @if (!isCreating) {
                      <span>Create account</span>
                    } @else {
                      <span>Creating...</span>
                    }
                  </button>
                </div>
              </form>
            } @else if (selectedUser) {
              @if (saveError) {
                <div class="alert alert-danger mb-0">{{ saveError }}</div>
              }
              @if (editingOwnAccount) {
                <div class="alert alert-warning mb-0">
                  Your own platform role and subscription are managed through the dedicated profile and billing flows.
                </div>
              }

              <form class="d-grid gap-3" (ngSubmit)="saveSelectedUser()">
                <div>
                  <label class="form-label">Email</label>
                  <input class="form-control" [value]="selectedUser.email" readonly />
                </div>

                <div class="row g-3">
                  <div class="col-sm-6">
                    <label class="form-label">Name</label>
                    <input class="form-control" [(ngModel)]="editForm.name" name="edit_name" />
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label">Nickname</label>
                    <input class="form-control" [(ngModel)]="editForm.nickname" name="edit_nickname" />
                  </div>
                </div>

                <div class="row g-3">
                  <div class="col-sm-6">
                    <label class="form-label">Role</label>
                    <select class="form-select" [(ngModel)]="editForm.role" name="edit_role" [disabled]="editingOwnAccount">
                      @for (role of roles; track role) {
                        <option [ngValue]="role">{{ role }}</option>
                      }
                    </select>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label">Plan</label>
                    <select class="form-select" [(ngModel)]="editForm.planCode" name="edit_plan" [disabled]="editingOwnAccount">
                      @for (plan of plans; track plan.code) {
                        <option [ngValue]="plan.code">{{ plan.name }}</option>
                      }
                    </select>
                  </div>
                </div>

                <div class="row g-3">
                  <div class="col-sm-6">
                    <label class="form-label">Plan status</label>
                    <select class="form-select" [(ngModel)]="editForm.planStatus" name="edit_plan_status" [disabled]="editingOwnAccount">
                      @for (status of planStatuses; track status) {
                        <option [ngValue]="status">{{ status }}</option>
                      }
                    </select>
                  </div>
                  <div class="col-sm-6 d-flex align-items-end">
                    <label class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        [(ngModel)]="editForm.isActive"
                        name="edit_active"
                        [disabled]="editingOwnAccount"
                      />
                      <span class="form-check-label">Account is active</span>
                    </label>
                  </div>
                </div>

                <div class="d-flex flex-wrap gap-2 justify-content-end">
                  <button type="button" class="btn btn-outline-secondary" (click)="closeModal()">Cancel</button>
                  <button class="btn btn-primary" type="submit" [disabled]="isSaving || editingOwnAccount">
                    @if (!isSaving) {
                      <span>Save changes</span>
                    } @else {
                      <span>Saving...</span>
                    }
                  </button>
                </div>
              </form>
            }
          </div>
        </section>
      </div>
    }
  }
</div>
